import type {
  AnyDatapoint,
  DatapointMetadata,
  SeriesId,
  XYChartAxisInternal,
  XYChartStackingValueBoundaries,
} from '../../types/xy-chart-internals.js';
import type { AreaDatapoint, BarDatapoint } from '../../types/xy-chart.js';
import {
  getCombinedAxesOrientationKey,
  getStackingData,
} from '../utils/stacking/stack-datapoints.js';

export interface StackingResult {
  calcDatapoint: AnyDatapoint;
  datapointMetadata: DatapointMetadata;
  shouldContinue: boolean;
}

/**
 * A class that encapsulates stacking operations for series variants.
 * This class can be instantiated by any series strategy that needs stacking functionality.
 */
interface StackDomainData {
  totalPositive: number;
  totalNegative: number;
  axisKey: string;
}

/** Per-axis state tracked during preprocessing.
 * `seriesIds.size === 1` ⇒ single-series axis: `getStackBinDomain` falls back to
 * `maxBinAbsValue` as a cross-bin reference so the tallest bar represents 100%. */
interface AxisStats {
  seriesIds: Set<SeriesId>;
  maxBinAbsValue: number;
}

export class StackableSeriesHelper {
  private static readonly STARTING_POINT = 0;
  private readonly stackGroups: Map<string, XYChartStackingValueBoundaries>;
  private readonly stackDomainData: Map<string, StackDomainData>;
  private readonly axisStats: Map<string, AxisStats>;
  /**
   * Per-instance cache of the `xAxisId-yAxisId-orientation` axis-key string,
   * keyed by the same triple. Instance-scoped (fresh per render, no
   * cross-render leakage, React-strict-mode safe).
   */
  private readonly axisOrientationKeyCache: Map<string, string>;

  constructor() {
    this.stackGroups = new Map();
    this.stackDomainData = new Map();
    this.axisStats = new Map();
    this.axisOrientationKeyCache = new Map();
  }

  private getCachedAxisOrientationKey(
    xAxisId: string,
    yAxisId: string,
    chartOrientation: 'vertical' | 'horizontal',
  ): string {
    const cacheKey = `${xAxisId}|${yAxisId}|${chartOrientation}`;
    let key = this.axisOrientationKeyCache.get(cacheKey);
    if (key === undefined) {
      key = getCombinedAxesOrientationKey(xAxisId, yAxisId, chartOrientation);
      this.axisOrientationKeyCache.set(cacheKey, key);
    }
    return key;
  }

  /**
   * Accumulates total positive and negative values per stack key.
   * Should be called during the preprocess phase before processing individual datapoints.
   * This is needed so that binDomain can be included in metadata for relative stacking.
   *
   * Also records which series contributed to each axisKey so that `getStackBinDomain` can
   * fall back to a cross-bin reference for single-series axes. Single-series stacks would
   * otherwise collapse to 100% in every bin.
   */
  processStackDomain(
    datapoint: BarDatapoint | AreaDatapoint,
    chartOrientation: 'vertical' | 'horizontal',
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    seriesId: SeriesId,
  ): void {
    const axisKey = this.getCachedAxisOrientationKey(
      xAxis.id,
      yAxis.id,
      chartOrientation,
    );
    const { stackKey, value } = getStackingData(
      chartOrientation,
      datapoint,
      xAxis,
      yAxis,
      axisKey,
    );

    const stats = this.getOrInitAxisStats(axisKey);
    stats.seriesIds.add(seriesId);

    if (typeof value === 'number') {
      const acc = this.stackDomainData.get(stackKey) ?? {
        totalPositive: StackableSeriesHelper.STARTING_POINT,
        totalNegative: StackableSeriesHelper.STARTING_POINT,
        axisKey,
      };
      if (value >= 0) {
        acc.totalPositive += value;
      } else {
        acc.totalNegative += Math.abs(value);
      }
      this.stackDomainData.set(stackKey, acc);

      const binAbsValue = Math.max(acc.totalPositive, acc.totalNegative);
      if (binAbsValue > stats.maxBinAbsValue) {
        stats.maxBinAbsValue = binAbsValue;
      }
    }
  }

  /**
   * Returns the total bin domain for a given stack key.
   *
   * For axes with more than one contributing series the result is the per-bin total height
   * of the stack (|positive| + |negative| within the bin). For axes with only one contributing
   * series the result collapses to a cross-bin reference: the largest per-bin absolute total
   * across all bins of that axis. This keeps single-series stacks consistent with the regular
   * relative behavior, where the tallest bar represents 100% and the rest scale proportionally.
   */
  getStackBinDomain(stackKey: string): number | undefined {
    const domainData = this.stackDomainData.get(stackKey);
    if (!domainData) {
      return undefined;
    }

    const stats = this.axisStats.get(domainData.axisKey);
    if (stats && stats.seriesIds.size === 1) {
      return stats.maxBinAbsValue;
    }

    return domainData.totalPositive + domainData.totalNegative;
  }

  private getOrInitAxisStats(axisKey: string): AxisStats {
    let stats = this.axisStats.get(axisKey);
    if (!stats) {
      stats = { seriesIds: new Set(), maxBinAbsValue: 0 };
      this.axisStats.set(axisKey, stats);
    }
    return stats;
  }

  /**
   * Processes a datapoint for stacking, calculating the offset and updating stack groups.
   * Returns the calculated datapoint with stacking offsets applied and enriched metadata.
   */
  processStackedDatapoint(
    datapoint: BarDatapoint | AreaDatapoint,
    chartOrientation: 'vertical' | 'horizontal',
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    datapointMetadata: DatapointMetadata,
  ): StackingResult {
    const axisKey = this.getCachedAxisOrientationKey(
      xAxis.id,
      yAxis.id,
      chartOrientation,
    );
    const { stackKey, value } = getStackingData(
      chartOrientation,
      datapoint,
      xAxis,
      yAxis,
      axisKey,
    );

    if (typeof value !== 'number') {
      return {
        calcDatapoint: {
          x0: datapoint.x0,
          x1: datapoint.x1,
          y0: datapoint.y0,
          y1: datapoint.y1,
        },
        datapointMetadata,
        shouldContinue: true,
      };
    }

    const prevValue = this.stackGroups.get(stackKey) ?? {
      minBoundary: StackableSeriesHelper.STARTING_POINT,
      maxBoundary: StackableSeriesHelper.STARTING_POINT,
    };

    const binDomain = this.getStackBinDomain(stackKey);

    const updatedMetadata: DatapointMetadata = {
      ...datapointMetadata,
      accumulator: value > 0 ? prevValue.maxBoundary : prevValue.minBoundary,
      binDomain,
    };

    const stackingOffset =
      value > 0 ? prevValue.maxBoundary : prevValue.minBoundary;

    const calcDatapoint: BarDatapoint = {
      x0:
        chartOrientation === 'horizontal' && typeof datapoint.x0 === 'number'
          ? datapoint.x0 + stackingOffset
          : datapoint.x0,
      x1:
        chartOrientation === 'horizontal' && typeof datapoint.x1 === 'number'
          ? datapoint.x1 + stackingOffset
          : datapoint.x1,
      y0:
        chartOrientation === 'vertical' && typeof datapoint.y0 === 'number'
          ? datapoint.y0 + stackingOffset
          : datapoint.y0,
      y1:
        chartOrientation === 'vertical' && typeof datapoint.y1 === 'number'
          ? datapoint.y1 + stackingOffset
          : datapoint.y1,
    };

    // In-place mutation of the per-stack accumulator. Safe because we already
    // captured `accumulator` and `stackingOffset` from prevValue above.
    if (value < 0) {
      prevValue.minBoundary += value;
    } else if (value > 0) {
      prevValue.maxBoundary += value;
    }
    this.stackGroups.set(stackKey, prevValue);

    return {
      calcDatapoint,
      datapointMetadata: updatedMetadata,
      shouldContinue: false,
    };
  }
}
